<template>
  <section class="empleado-panel">
    <div v-if="error" class="mod-alert-error">
      <i class="bi bi-exclamation-triangle-fill"></i>
      {{ error }}
    </div>

    <div class="empleado-upload-form">
      <div class="mod-field">
        <label>Archivo <span class="mod-required">*</span></label>
        <input ref="archivoInput" type="file" @change="onArchivo">
      </div>
      <div class="mod-field empleado-wide">
        <label>Tipo de documento</label>
        <CatSearch
          catalogo="empleado_archivo_tipo"
          v-model="tipoArchivo"
          placeholder="Buscar tipo..."
          display-field="nombre"
          :min-length="0"
        />
      </div>
      <div class="mod-field mod-field-check">
        <label class="mod-check-label">
          <input v-model="esPrincipal" type="checkbox">
          <span>Principal</span>
        </label>
      </div>
      <div class="mod-field">
        <label>Fecha del documento</label>
        <input v-model="fechaDocumento" type="date">
      </div>
      <div class="mod-field mod-field-check">
        <label class="mod-check-label">
          <input v-model="vence" type="checkbox">
          <span>Vence</span>
        </label>
      </div>
      <div class="mod-field">
        <label>Fecha de vencimiento</label>
        <input v-model="fechaVencimiento" type="date" :disabled="!vence">
      </div>
      <div class="mod-field empleado-wide">
        <label>Observaciones</label>
        <input v-model="observaciones" type="text" maxlength="500" placeholder="Notas del archivo">
      </div>
      <div class="empleado-panel-actions">
        <button type="button" class="mod-btn mod-btn-save" :disabled="saving || !archivo" @click="subir">
          <span v-if="saving" class="mod-spinner-sm"></span>
          <template v-else>
            <i class="bi bi-upload"></i>
            Subir archivo
          </template>
        </button>
      </div>
    </div>

    <div class="empleado-table-wrap">
      <div v-if="loading" class="empleado-empty">Cargando expediente...</div>
      <div v-else-if="items.length === 0" class="empleado-empty">Sin archivos en expediente</div>
      <table v-else class="empleado-table">
        <thead>
          <tr>
            <th>Archivo</th>
            <th>Tipo</th>
            <th>Tamaño</th>
            <th>Fecha</th>
            <th>Vencimiento</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in items" :key="item.EmpleadoArchivoId">
            <td>
              <strong>{{ item.NombreOriginal }}</strong>
              <span v-if="item.EsPrincipal" class="empleado-badge">Principal</span>
              <div v-if="item.Observaciones" class="mod-td-muted">{{ item.Observaciones }}</div>
            </td>
            <td>{{ item.TipoArchivo || '-' }}</td>
            <td>{{ formatoTamano(item.TamanoBytes) }}</td>
            <td>{{ formatoFecha(item.FechaDocumento || item.FechaInserta) }}</td>
            <td>
              <span :class="vencimientoClass(item)">
                {{ textoVencimiento(item) }}
              </span>
            </td>
            <td class="empleado-row-actions">
              <button v-if="service.esVisualizable(item)" type="button" class="empleado-icon-btn" title="Ver" @click="visualizar(item)">
                <i class="bi bi-eye"></i>
              </button>
              <button type="button" class="empleado-icon-btn" title="Descargar" @click="descargar(item)">
                <i class="bi bi-download"></i>
              </button>
              <button type="button" class="empleado-icon-btn danger" title="Eliminar" @click="borrar(item)">
                <i class="bi bi-trash"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script>
import { showToast } from '@/shared/utils/toast';
import CatSearch from '@/shared/components/CatSearch.vue';
import empleadoGestionService from './empleadoGestion.service';

export default {
  name: 'EmpleadoArchivosPanel',
  components: { CatSearch },
  props: {
    empleadoClave: { type: String, required: true }
  },
  data() {
    return {
      service: empleadoGestionService,
      items: [],
      archivo: null,
      tipoArchivo: '',
      observaciones: '',
      fechaDocumento: '',
      vence: false,
      fechaVencimiento: '',
      esPrincipal: false,
      loading: false,
      saving: false,
      error: null
    };
  },
  created() {
    this.cargar();
  },
  methods: {
    async cargar() {
      this.loading = true;
      try {
        this.items = await empleadoGestionService.listarArchivos(this.empleadoClave);
      } catch {
        this.error = 'No fue posible cargar el expediente';
      } finally {
        this.loading = false;
      }
    },
    onArchivo(event) {
      this.archivo = event.target.files?.[0] || null;
    },
    limpiar() {
      this.archivo = null;
      this.tipoArchivo = '';
      this.observaciones = '';
      this.fechaDocumento = '';
      this.vence = false;
      this.fechaVencimiento = '';
      this.esPrincipal = false;
      if (this.$refs.archivoInput) this.$refs.archivoInput.value = '';
    },
    async subir() {
      if (!this.archivo) return;
      this.saving = true;
      this.error = null;
      try {
        await empleadoGestionService.subirArchivo(this.archivo, this.empleadoClave, {
          tipoArchivo: this.tipoArchivo,
          observaciones: this.observaciones,
          fechaDocumento: this.fechaDocumento,
          vence: this.vence,
          fechaVencimiento: this.vence ? this.fechaVencimiento : '',
          esPrincipal: this.esPrincipal
        });
        showToast('Archivo subido correctamente', 'success');
        this.limpiar();
        await this.cargar();
      } catch {
        this.error = 'Error al subir archivo';
      } finally {
        this.saving = false;
      }
    },
    async descargar(item) {
      try {
        await empleadoGestionService.descargarArchivo(item);
      } catch {
        this.error = 'No fue posible descargar el archivo';
      }
    },
    async visualizar(item) {
      try {
        await empleadoGestionService.visualizarArchivo(item);
      } catch {
        this.error = 'No fue posible visualizar el archivo';
      }
    },
    async borrar(item) {
      if (!confirm('¿Eliminar este archivo del expediente?')) return;
      try {
        const result = await empleadoGestionService.borrarArchivo(item);
        if (result.Number >= 0) {
          showToast(result.Message || 'Archivo eliminado', result.Type || 'success');
          await this.cargar();
        } else {
          this.error = result.Message || 'Error al eliminar archivo';
        }
      } catch {
        this.error = 'Error de conexión con el servidor';
      }
    },
    formatoTamano(bytes) {
      const n = Number(bytes || 0);
      if (n < 1024) return `${n} B`;
      if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
      return `${(n / 1024 / 1024).toFixed(1)} MB`;
    },
    formatoFecha(fecha) {
      if (!fecha) return '-';
      return new Date(fecha).toLocaleDateString();
    },
    textoVencimiento(item) {
      if (!item.Vence) return 'No vence';
      if (!item.FechaVencimiento) return 'Sin fecha';
      return this.formatoFecha(item.FechaVencimiento);
    },
    vencimientoClass(item) {
      if (!item.Vence || !item.FechaVencimiento) return 'mod-td-muted';
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);
      const fecha = new Date(item.FechaVencimiento);
      if (fecha < hoy) return 'empleado-vencido';
      const dias = Math.ceil((fecha - hoy) / 86400000);
      return dias <= 30 ? 'empleado-por-vencer' : 'mod-td-muted';
    }
  }
};
</script>

<style scoped>
@import "@/modules/_shared/catalog.css";
@import "./empleadoGestion.css";
</style>
